<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppErrorState from '@/components/app-error-state'
import LoginPrompt from '@/components/login-prompt'
import ProfileCartoonCard from '@/components/profile-cartoon-card'
import ProfileWorkFilterDropdown from '@/components/profile-work-filter-dropdown'
import ProfileUserHeader from '@/components/profile-user-header'
import SummaryContentCard from '@/components/summary-content-card'
import {
  PROFILE_WORK_FILTERS,
  useUserProfile,
  type ProfileWorkFilter,
} from '@/composables/useUserProfile'
import { useElementVisibility } from '@/composables/useElementVisibility'
import { useRouteRefreshSignal } from '@/composables/useRouteRefreshSignal'
import { useScreenSize } from '@/composables/useScreenSize'
import { useThemeStore } from '@/stores/theme'
import { useUserStore } from '@/stores/user'
import type { UserCartoonItem, UserWorkItem } from '@/api/home/type'
import { buildCartoonUrl } from '@/utils/distribute/navigation'
import { openLegacyPlayerUrl } from '@/utils/legacy-player'
import { buildSummaryContentViewUrl, resolveSummaryContentKind } from '@/utils/profile/content-card'
import { resolveSummaryContentColumnCount } from '@/utils/profile/content-grid'
import { applyDocumentTitle } from '@/utils/page-title'
import { getRuntimePerformanceBudget } from '@/utils/performance/runtime-budget'
import { stateImages } from '@/utils/state-images'
import loadingImage from '@/assets/images/profile-loading.webp'

defineOptions({
  name: 'ProfileView',
})

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const themeStore = useThemeStore()
const runtimeBudget = getRuntimePerformanceBudget()
const loadMoreTarget = shallowRef<HTMLElement | null>(null)
const { screenWidth } = useScreenSize()
const isMineRoute = computed(() => route.name === 'MeView')

function normalizeProfileId(id: string) {
  return id.trim().toLowerCase()
}

const profileId = computed(() => {
  if (isMineRoute.value) {
    return normalizeProfileId(userStore.getUid() || userStore.getId())
  }

  const queryUid = typeof route.query.uid === 'string' ? route.query.uid : ''
  const uidParam = typeof route.params.uid === 'string' ? route.params.uid : ''
  const userIdParam = typeof route.params.userId === 'string' ? route.params.userId : ''

  return normalizeProfileId(
    queryUid || uidParam || userIdParam || userStore.getUid() || userStore.getId(),
  )
})
const isMine = computed(() => {
  const id = profileId.value
  const myUid = normalizeProfileId(userStore.getUid())

  return Boolean(id && (isMineRoute.value || id === myUid || id === userStore.getId()))
})

const {
  profile,
  works,
  cartoons,
  activeTab,
  activeWorkFilter,
  loadingWorks,
  loadingMoreWorks,
  loadingCartoons,
  isInitialLoading,
  canLoadMoreWorks,
  error,
  worksHasNext,
  setActiveTab,
  setActiveWorkFilter,
  refresh,
  loadMoreWorks,
} = useUserProfile(profileId)
const { isVisible: isLoadMoreVisible } = useElementVisibility(loadMoreTarget, {
  rootMargin: runtimeBudget.isConstrained ? '240px 0px' : '420px 0px',
  threshold: 0,
})
const showInitialSkeleton = computed(() => isInitialLoading.value && works.value.length === 0)
const profileErrorType = computed(() => error.value?.type ?? null)
const showLoginPrompt = computed(
  () => !userStore.isLoggedIn() || profileErrorType.value === 'login-required',
)
const loginPromptMessage = computed(() =>
  isMineRoute.value ? '登录后查看你的主页' : '登录后查看这个主页',
)
const showProfileTabs = computed(() => !showLoginPrompt.value && profileErrorType.value === null)
const showWorksEmpty = computed(
  () => !loadingWorks.value && works.value.length === 0 && !error.value && !showLoginPrompt.value,
)
const showCartoonsEmpty = computed(
  () =>
    activeTab.value === 'cartoons' &&
    !loadingCartoons.value &&
    cartoons.value.length === 0 &&
    !error.value &&
    !showLoginPrompt.value,
)
const activeWorkFilterLabel = computed(
  () =>
    PROFILE_WORK_FILTERS.find((filter) => filter.key === activeWorkFilter.value)?.label ?? '作品',
)
const stateMessage = computed(() => {
  if (activeTab.value === 'cartoons') {
    return '还没有角色'
  }

  return activeWorkFilter.value === 'all'
    ? '还没有作品'
    : `还没有${activeWorkFilterLabel.value}作品`
})
const workTabCount = computed(() =>
  activeWorkFilter.value === 'all' ? (profile.value?.workCount ?? works.value.length) : undefined,
)
const workColumnCount = computed(() => resolveSummaryContentColumnCount(screenWidth.value))
const workGridStyle = computed(() => ({ '--profile-grid-columns': String(workColumnCount.value) }))
const cartoonGridStyle = computed(() => ({
  '--profile-grid-columns': String(workColumnCount.value),
}))
const workColumns = computed(() => distributeWorks(works.value, workColumnCount.value))
const workSkeletonColumns = computed(() =>
  distributeByIndex(
    Array.from({ length: 10 }, (_, index) => index),
    workColumnCount.value,
  ),
)
const cartoonSkeletonItems = computed(() =>
  Array.from({ length: workColumnCount.value * 2 }, (_, index) => index),
)

useRouteRefreshSignal('me', async () => {
  if (!isMineRoute.value) {
    return
  }

  await refresh()
})

function openWork(item: UserWorkItem) {
  const url = buildSummaryContentViewUrl(item, themeStore.currentTheme)

  if (url) {
    openLegacyPlayerUrl(url)
  }
}

function openAuthor(item: UserWorkItem) {
  const uid = item.user?.uid

  if (!uid) {
    return
  }

  void router.push(`/${uid}`)
}

function openCartoon(item: UserCartoonItem) {
  const url = buildCartoonUrl(item)

  if (!url) {
    return
  }

  void router.push(url)
}

function handleWorkFilterChange(filter: string) {
  setActiveWorkFilter(filter as ProfileWorkFilter)
}

function backToFeed() {
  void router.replace('/feed')
}

function distributeByIndex<T>(items: T[], count: number): T[][] {
  const columns = Array.from({ length: Math.max(1, count) }, () => [] as T[])

  items.forEach((item, index) => {
    columns[index % columns.length]?.push(item)
  })

  return columns
}

function distributeWorks(items: UserWorkItem[], count: number): UserWorkItem[][] {
  const columns = Array.from({ length: Math.max(1, count) }, () => [] as UserWorkItem[])
  const heights = Array.from({ length: columns.length }, () => 0)

  items.forEach((item) => {
    const targetIndex = heights.indexOf(Math.min(...heights))
    const nextHeight = (heights[targetIndex] ?? 0) + estimateWorkHeight(item)

    columns[targetIndex]?.push(item)
    heights[targetIndex] = nextHeight
  })

  return columns
}

function estimateWorkHeight(item: UserWorkItem) {
  const kind = resolveSummaryContentKind(item)

  if (kind === 'radio') {
    return 1.38
  }

  if (kind === 'media-imgtxt') {
    return 1.72
  }

  return 2.0
}

watch([isLoadMoreVisible, canLoadMoreWorks], ([visible, canLoadMore]) => {
  if (visible && canLoadMore) {
    void loadMoreWorks()
  }
})

watch(
  [() => profile.value?.nickname ?? '', isMineRoute, () => userStore.getNickname()],
  ([profileNickname, mineRoute, myNickname]) => {
    const title = profileNickname || (mineRoute ? myNickname : '')

    if (title) {
      applyDocumentTitle(title)
    }
  },
  { immediate: true },
)
</script>

<template>
  <section class="profile-page">
    <div class="profile-page__inner">
      <ProfileUserHeader v-if="profile && !showLoginPrompt" :profile="profile" :is-mine="isMine" />

      <div
        v-else-if="!showLoginPrompt && isInitialLoading"
        class="profile-page__header-skeleton"
        aria-label="用户信息加载中"
      >
        <div class="profile-page__avatar-group-skeleton">
          <div class="profile-page__avatar-skeleton" />
          <div class="profile-page__clone-skeleton" />
        </div>
        <div class="profile-page__line-skeleton is-name" />
        <div class="profile-page__line-skeleton" />
        <div class="profile-page__line-skeleton is-wide" />
      </div>

      <div
        v-if="showProfileTabs"
        class="profile-page__tabs"
        role="tablist"
        aria-label="用户主页分类"
      >
        <ProfileWorkFilterDropdown
          :active="activeTab === 'works'"
          :count="workTabCount"
          :model-value="activeWorkFilter"
          :options="PROFILE_WORK_FILTERS"
          @activate="setActiveTab('works')"
          @update:model-value="handleWorkFilterChange"
        />
        <button
          type="button"
          class="profile-page__tab"
          :class="{ 'is-active': activeTab === 'cartoons' }"
          role="tab"
          :aria-selected="activeTab === 'cartoons'"
          @click="setActiveTab('cartoons')"
        >
          角色
        </button>
      </div>

      <div class="profile-page__content">
        <div v-if="showLoginPrompt" class="profile-page__login">
          <LoginPrompt :message="loginPromptMessage" />
        </div>

        <div
          v-else-if="profileErrorType === 'not-found'"
          class="profile-page__state profile-page__state--not-found"
        >
          <img
            class="profile-page__state-image"
            :src="stateImages.empty"
            alt="用户不存在"
            width="160"
            height="160"
            draggable="false"
          />
          <div class="profile-page__state-copy">
            <h2>这个用户不存在</h2>
            <p>链接可能已失效，回到发现页看看其他人。</p>
          </div>
          <button type="button" @click="backToFeed">回到发现页</button>
        </div>

        <AppErrorState v-else-if="error" variant="full" @retry="refresh" />

        <div v-else-if="activeTab === 'works'" class="profile-page__grid" :style="workGridStyle">
          <template v-if="showInitialSkeleton">
            <div
              v-for="(column, columnIndex) in workSkeletonColumns"
              :key="`work-skeleton-column-${columnIndex}`"
              class="profile-page__column"
            >
              <div v-for="index in column" :key="index" class="profile-page__card-skeleton">
                <div class="profile-page__cover-skeleton" />
                <div class="profile-page__card-body-skeleton">
                  <div class="profile-page__line-skeleton is-card-title" />
                  <div class="profile-page__line-skeleton is-card-author" />
                  <div class="profile-page__line-skeleton is-card-short" />
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <div
              v-for="(column, columnIndex) in workColumns"
              :key="`work-column-${columnIndex}`"
              class="profile-page__column"
            >
              <SummaryContentCard
                v-for="(item, index) in column"
                :key="item.id"
                :item="item"
                :priority="!runtimeBudget.isConstrained && columnIndex === 0 && index < 2"
                @click="openWork"
                @author-click="openAuthor"
              />
            </div>
          </template>
        </div>

        <div v-else class="profile-page__cartoon-grid" :style="cartoonGridStyle">
          <template v-if="loadingCartoons && cartoons.length === 0">
            <div
              v-for="index in cartoonSkeletonItems"
              :key="`cartoon-skeleton-${index}`"
              class="profile-page__card-skeleton profile-page__card-skeleton--cartoon"
            >
              <div class="profile-page__cartoon-cover-skeleton" />
            </div>
          </template>

          <template v-else>
            <ProfileCartoonCard
              v-for="item in cartoons"
              :key="item.id"
              :item="item"
              @click="openCartoon"
            />
          </template>
        </div>

        <div
          v-if="showWorksEmpty || showCartoonsEmpty"
          class="profile-page__state profile-page__state--fill"
        >
          <img
            class="profile-page__state-image"
            :src="stateImages.empty"
            alt="空状态"
            width="160"
            height="160"
            draggable="false"
          />
          <p>{{ stateMessage }}</p>
        </div>

        <div
          v-if="activeTab === 'works' && works.length > 0 && (worksHasNext || loadingMoreWorks)"
          ref="loadMoreTarget"
          class="profile-page__loading"
          aria-live="polite"
        >
          <img
            v-if="loadingMoreWorks"
            class="profile-page__loading-image"
            :src="loadingImage"
            alt="加载中"
            width="64"
            height="64"
            draggable="false"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.profile-page {
  min-width: 0;
  min-height: calc(100vh - 16px);
  border-radius: 18px;
  background: var(--profile-bg);
  color: var(--profile-text);
}

.profile-page__inner {
  display: flex;
  min-height: inherit;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
}

.profile-page__tabs {
  position: sticky;
  top: 0;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: var(--profile-bg);
}

.profile-page__content {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.profile-page__login {
  display: flex;
  min-height: calc(100vh - 96px);
  flex: 1 1 auto;
}

.profile-page__tab {
  position: relative;
  padding: 12px 12px 8px;
  color: var(--profile-muted);
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}

.profile-page__tab.is-active {
  color: var(--profile-text);
}

.profile-page__tab.is-active::after {
  position: absolute;
  right: 12px;
  bottom: 3px;
  left: 12px;
  height: 1px;
  border-radius: 999px;
  background: var(--profile-text);
  content: '';
}

.profile-page__grid {
  display: grid;
  grid-template-columns: repeat(var(--profile-grid-columns), minmax(0, 1fr));
  gap: 0;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

.profile-page__cartoon-grid {
  display: grid;
  grid-template-columns: repeat(var(--profile-grid-columns), minmax(0, 1fr));
  gap: 0;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

.profile-page__column {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.profile-page__state {
  display: flex;
  min-height: 240px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--profile-muted);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  padding: 24px 16px;
}

.profile-page__state--fill {
  min-height: 0;
  flex: 1 1 auto;
}

.profile-page__state--not-found {
  min-height: calc(100vh - 96px);
  gap: 12px;
  animation: profile-state-enter 0.26s ease both;
}

.profile-page__state-copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: min(100%, 360px);
}

.profile-page__state-copy h2 {
  margin: 0;
  color: var(--profile-text);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.profile-page__state p {
  margin: 0;
}

.profile-page__state-image {
  width: var(--app-state-illustration-size);
  height: var(--app-state-illustration-size);
  object-fit: contain;
  opacity: 0.72;
}

.profile-page__state--not-found .profile-page__state-image {
  width: var(--app-state-illustration-size-large);
  height: var(--app-state-illustration-size-large);
  opacity: 1;
}

.profile-page__state button {
  display: inline-flex;
  min-width: 128px;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
  border-radius: 999px;
  background: var(--profile-text);
  color: var(--profile-bg);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  outline: none;
  padding: 8px 18px;
  transition:
    opacity var(--app-interaction-duration) var(--app-interaction-ease),
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.profile-page__state button:hover,
.profile-page__state button:focus-visible {
  opacity: 0.86;
}

.profile-page__state button:focus-visible {
  box-shadow:
    0 0 0 3px var(--profile-bg),
    0 0 0 5px var(--profile-text);
}

.profile-page__state button:active {
  transform: scale(var(--app-interaction-standard-active-scale));
}

.profile-page__loading {
  display: flex;
  min-height: 112px;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}

.profile-page__loading-image {
  width: 64px;
  height: 64px;
  object-fit: cover;
  opacity: 0.72;
}

.profile-page__header-skeleton {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 24px 22px;
}

.profile-page__avatar-group-skeleton,
.profile-page__avatar-skeleton,
.profile-page__clone-skeleton,
.profile-page__line-skeleton,
.profile-page__cover-skeleton,
.profile-page__cartoon-cover-skeleton {
  position: relative;
  overflow: hidden;
}

.profile-page__avatar-skeleton::after,
.profile-page__clone-skeleton::after,
.profile-page__line-skeleton::after,
.profile-page__cover-skeleton::after,
.profile-page__cartoon-cover-skeleton::after {
  display: block;
  width: 100%;
  height: 100%;
  animation: profile-shimmer 1.9s ease-in-out infinite;
  background: linear-gradient(
    110deg,
    rgb(255 255 255 / 0) 0%,
    rgb(255 255 255 / 0) 38%,
    var(--app-skeleton-shimmer) 50%,
    rgb(255 255 255 / 0) 62%,
    rgb(255 255 255 / 0) 100%
  );
  content: '';
  transform: translateX(-120%);
}

.profile-page__avatar-group-skeleton {
  position: relative;
  width: 180px;
  height: 180px;
}

.profile-page__avatar-skeleton {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: var(--app-skeleton-base);
  transform: translate(-50%, -50%);
}

.profile-page__clone-skeleton {
  position: absolute;
  top: 42px;
  right: 6px;
  width: 56px;
  height: 56px;
  border: 2px solid var(--profile-bg);
  border-radius: 50%;
  background: var(--app-skeleton-line);
}

.profile-page__line-skeleton {
  width: 130px;
  height: 16px;
  border-radius: 999px;
  background: var(--app-skeleton-line);
}

.profile-page__line-skeleton.is-name {
  width: 86px;
  height: 24px;
}

.profile-page__line-skeleton.is-wide {
  width: min(320px, 70vw);
}

.profile-page__card-skeleton {
  display: inline-flex;
  width: 100%;
  break-inside: avoid;
  flex-direction: column;
  gap: 0;
  padding: 12px 12px 16px;
}

.profile-page__card-skeleton--cartoon {
  padding: 12px;
}

.profile-page__cover-skeleton {
  aspect-ratio: 293 / 468.8;
  border-radius: 12px;
  background: var(--app-skeleton-base);
}

.profile-page__card-body-skeleton {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 8px 0;
}

.profile-page__cartoon-cover-skeleton {
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  background: var(--app-skeleton-base);
}

.profile-page__line-skeleton.is-card-title {
  width: 78%;
  height: 18px;
}

.profile-page__line-skeleton.is-card-author {
  width: 42%;
}

.profile-page__line-skeleton.is-card-short {
  width: 36%;
  height: 12px;
}

@keyframes profile-shimmer {
  0% {
    transform: translateX(-120%);
  }

  52%,
  to {
    transform: translateX(120%);
  }
}

@keyframes profile-state-enter {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 900px) {
  .profile-page {
    border-radius: 14px;
  }
}

@media (max-width: 640px) {
  .profile-page {
    min-height: calc(100vh - 8px);
    border-radius: 0;
  }

  .profile-page__tabs {
    top: 0;
  }

  .profile-page__tab {
    padding: 10px 12px 6px;
    font-size: 15px;
    line-height: 22px;
  }

  .profile-page__filters {
    justify-content: flex-start;
    overflow-x: auto;
    padding: 4px 10px 8px;
  }

  .profile-page__filter {
    flex: 0 0 auto;
  }

  .profile-page__grid,
  .profile-page__grid,
  .profile-page__cartoon-grid {
    padding: 0 6px;
  }

  .profile-page__cartoon-grid {
    gap: 0;
  }

  .profile-page__header-skeleton {
    padding: 28px 16px 18px;
  }

  .profile-page__avatar-group-skeleton {
    width: 150px;
    height: 150px;
  }

  .profile-page__clone-skeleton {
    top: 36px;
    right: 4px;
    width: 48px;
    height: 48px;
  }

  .profile-page__card-body-skeleton {
    padding: 7px 2px 0;
  }

  .profile-page__card-skeleton--cartoon {
    padding: 7px 5px 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .profile-page__state--not-found {
    animation: none;
  }

  .profile-page__avatar-skeleton::after,
  .profile-page__clone-skeleton::after,
  .profile-page__line-skeleton::after,
  .profile-page__cover-skeleton::after,
  .profile-page__cartoon-cover-skeleton::after {
    animation: none;
  }
}
</style>
